// TanStack Query client + persistor.
// Кэшируем серверный state в localStorage, чтобы при перезагрузке UI
// мгновенно показывал последние данные (optimistic hydration).
//
// Whitelist: персистим только небольшие/важные query keys, чтобы не раздуть
// localStorage. Правила — `query-persist-whitelist.ts` (tRPC-пути + auth).
//
// onlineManager связан с `lib/network-status` (probe `/version.json`), а не
// только с `navigator.onLine` — иначе после offline+F5 queries остаются paused.

import { persistQueryClient } from "@tanstack/query-persist-client-core";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { onlineManager, QueryClient } from "@tanstack/react-query";
import { resolveOnlineStatus, subscribeOnlineStatus } from "@/lib/network-status";
import {
	PERSIST_STRING_ROOTS,
	PERSIST_TRPC_PATHS,
	shouldPersistQuery,
} from "@/lib/query-persist-whitelist";

const isBrowser = typeof window !== "undefined";

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			gcTime: 5 * 60_000,
			retry: 1,
			refetchOnWindowFocus: false,
			refetchOnReconnect: true,
		},
		mutations: {
			retry: 0,
		},
	},
});

/**
 * Сколько запись whitelist-запроса живёт в кэше без подписчиков.
 *
 * Persist сохраняет СНАПШОТ кэша, поэтому общий `gcTime` (5 мин) молча
 * обесценивал персист: стоило уйти с главной на пять минут — запись
 * выбрасывалась из памяти, следующий снапшот уходил в localStorage уже без неё,
 * и холодный старт снова ждал сеть. Держим ровно те ключи, ради которых персист
 * и заведён, столько же, сколько живёт сам снапшот (`maxAge` 24ч).
 */
const PERSISTED_QUERY_GC_TIME = 24 * 60 * 60 * 1000;

for (const path of PERSIST_TRPC_PATHS) {
	// tRPC-ключ: [["module","proc"], {input,type}] — префикс матчится частично.
	queryClient.setQueryDefaults([path.split(".")], { gcTime: PERSISTED_QUERY_GC_TIME });
}
for (const root of PERSIST_STRING_ROOTS) {
	queryClient.setQueryDefaults([root], { gcTime: PERSISTED_QUERY_GC_TIME });
}

if (isBrowser) {
	onlineManager.setEventListener((setOnline) => {
		void resolveOnlineStatus().then(setOnline);
		return subscribeOnlineStatus(setOnline);
	});
}

if (isBrowser) {
	const persister = createSyncStoragePersister({
		storage: window.localStorage,
		key: "studyhub:query-cache",
		throttleTime: 1000,
	});

	void persistQueryClient({
		queryClient,
		persister,
		maxAge: 24 * 60 * 60 * 1000, // 24h
		dehydrateOptions: {
			shouldDehydrateQuery: shouldPersistQuery,
		},
	});
}
